// The WebGL wing planes' pose. Every frame, `MonarchMotion`'s folds turn the two rigid planes
// around fitted hinges, so the fallback flaps the same way the WebGPU monarch does.

import {
  WING_FAR_BASE_Y_ANGLE,
  type Vec3,
  type WingPlaneRotation,
  type WingStats,
} from "@bfly/engine";
import { MonarchMotion, type MonarchPose } from "./monarch-motion.js";

export interface WingPlanePose {
  near: WingPlaneRotation;
  far: WingPlaneRotation;
  // How far each plane has turned around its hinge from rest, in radians.
  nearTurnRad: number;
  farTurnRad: number;
}

type Beat = Pick<WingStats, "beatOn" | "beatStart" | "beatDur" | "kind">;

interface WingPlaneHinge {
  // Unit axis through the wing pivot, in plane space: x right, y toward the camera, z down
  // the image.
  readonly axis: Vec3;
  // How much the plane turns for each radian the forewing and hindwing fold from rest.
  readonly fore: number;
  readonly hind: number;
}

function hinge(imageTilt: number, cameraTilt: number, fore: number, hind: number): WingPlaneHinge {
  const a = (imageTilt * Math.PI) / 180;
  const b = (cameraTilt * Math.PI) / 180;
  return { axis: [Math.cos(b) * Math.sin(a), Math.sin(b), Math.cos(b) * Math.cos(a)], fore, hind };
}

// The WebGL fallback plays the WebGPU butterfly's motion (monarch-motion.ts) on its two rigid
// planes. Each plane turns around a fixed hinge, tilted like the model's body axis on screen,
// by a weighted sum of the forewing and hindwing folds.
//
// I picked the hinges (degrees from the image vertical, then toward the camera) and the
// weights with a least-squares fit of the projected planes to the model's projected wing
// outlines, over a flap, a flutter, and resting breaths. The planes stay within about 27px
// of the model's outline in the 1024px artwork. The old vertical hinge was off by up to 80px.
const WING_PLANE_HINGES = {
  near: hinge(31.7, -11.9, 0.686, 0.96),
  far: hinge(27.9, -15.9, 0.444, 0.549),
} as const;

// A motion that hasn't run yet is at rest.
const REST: Readonly<MonarchPose> = { ...new MonarchMotion().pose };

// Rotation by `angle` around a unit axis (Rodrigues), as a row-major 3x3 matrix.
function rotationInto(out: Float64Array, [x, y, z]: Vec3, angle: number): Float64Array {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const k = 1 - c;
  out.set([
    c + x * x * k,
    x * y * k - z * s,
    x * z * k + y * s,
    y * x * k + z * s,
    c + y * y * k,
    y * z * k - x * s,
    z * x * k - y * s,
    z * y * k + x * s,
    c + z * z * k,
  ]);
  return out;
}

function multiplyInto(out: Float64Array, a: Float64Array, b: Float64Array): Float64Array {
  for (let row = 0; row < 3; row++)
    for (let column = 0; column < 3; column++)
      out[row * 3 + column] =
        a[row * 3]! * b[column]! +
        a[row * 3 + 1]! * b[3 + column]! +
        a[row * 3 + 2]! * b[6 + column]!;
  return out;
}

// The far plane's base turn in the image, matching the y rotation `wingPlaneMatrixInto` draws.
const FAR_BASE = rotationInto(new Float64Array(9), [0, 1, 0], WING_FAR_BASE_Y_ANGLE);

// The exact inverse of `wingPlaneMatrixInto` (Rx·Ry·Rz, with its own z direction) for a
// row-major matrix.
function rotationFrom(m: Float64Array, out: WingPlaneRotation): void {
  out.xRad = Math.atan2(-m[5]!, m[8]!);
  out.yRad = Math.asin(Math.max(-1, Math.min(1, m[2]!)));
  out.zRad = Math.atan2(m[1]!, m[0]!);
}

export class WingPlaneMotion {
  private readonly motion = new MonarchMotion();
  private readonly turn = new Float64Array(9);
  private readonly composed = new Float64Array(9);
  // Updated in place every frame, so the render loop doesn't allocate here.
  readonly pose: WingPlanePose = {
    near: { xRad: 0, yRad: 0, zRad: 0 },
    far: { xRad: 0, yRad: WING_FAR_BASE_Y_ANGLE, zRad: 0 },
    nearTurnRad: 0,
    farTurnRad: 0,
  };

  update(now: number, beat: Beat, effectsOn: boolean): WingPlanePose {
    const model = this.motion.update(now, beat, effectsOn);
    const fore = REST.foreFold - model.foreFold;
    const hind = REST.hindFold - model.hindFold;
    this.pose.nearTurnRad = this.place(this.pose.near, WING_PLANE_HINGES.near, fore, hind, null);
    this.pose.farTurnRad = this.place(this.pose.far, WING_PLANE_HINGES.far, fore, hind, FAR_BASE);
    return this.pose;
  }

  private place(
    out: WingPlaneRotation,
    { axis, fore: foreWeight, hind: hindWeight }: WingPlaneHinge,
    fore: number,
    hind: number,
    base: Float64Array | null,
  ): number {
    const turn = foreWeight * fore + hindWeight * hind;
    // At rest, the planes hold their calibrated pose exactly.
    if (turn === 0) {
      out.xRad = 0;
      out.yRad = base ? WING_FAR_BASE_Y_ANGLE : 0;
      out.zRad = 0;
      return 0;
    }
    rotationInto(this.turn, axis, turn);
    rotationFrom(base ? multiplyInto(this.composed, base, this.turn) : this.turn, out);
    return turn;
  }
}
